/**
 * Coordinación → Externos: los coguías que no son del DCC, en sus tres estados.
 *
 * - **Pendientes**: anotados como texto en un tema y nada más. No son nadie para el
 *   sistema, no tienen cuenta y no pueden evaluar. Son la mayoría, y el botón les da
 *   acceso a todos sus temas de una vez: la misma persona suele estar en varios, a
 *   veces con el nombre escrito distinto, y hacerlo tema por tema era tantas vueltas
 *   como temas.
 * - **Registrados**: ya tienen ficha y cuenta. Acá la pregunta es otra: ¿alguno es, en
 *   realidad, alguien que ya estaba con otro correo? Pasa cuando entró con su gmail y
 *   volvió, contratado, con el institucional. Juntarlos es irreversible, así que
 *   siempre se mira antes de hacerlo.
 * - **Sin correo**: anotados solo con el nombre. No hay a quién darle acceso hasta
 *   conseguir el dato, así que se listan para poder ir a pedirlo.
 */
import { useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  TableBody,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { CeldaKit, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import Etiqueta from "../../components/kit/Etiqueta";
import EnlaceTema from "../../components/tema/EnlaceTema";
import { useDarAccesoPorCorreo, useExternosSinRut, useFusionarPersona } from "../../queries/usePersonas";
import { useDocentes } from "../../queries/useTemas";
import { CoguiaPendiente, ExternoSinRut } from "../../services/personaService";
import { DocenteOpcion } from "../../types/tema";
import { mensajeDeError } from "../../utils/errores";
import { usePestanaUrl } from "../../utils/estadoLista";
import { Accesores, ordenar, useOrden } from "../../utils/orden";

const ACC_PENDIENTE: Accesores<CoguiaPendiente> = {
  persona: (f) => f.nombres[0],
  email: (f) => f.email,
  temas: (f) => f.temas.length,
  // Los que ya están en el sistema primero al ordenar descendente: son los que hay
  // que mirar con más cuidado antes de apretar.
  ya_es: (f) => f.ya_es ?? "",
};

const ACC_REGISTRADO: Accesores<ExternoSinRut> = {
  persona: (e) => e.nombre,
  email: (e) => e.email,
  historia: (e) => e.temas + e.procesos + e.comisiones + e.evaluaciones,
};

const ACC_SIN_CORREO: Accesores<{ id: number; titulo: string; nombre: string }> = {
  persona: (f) => f.nombre,
  tema: (f) => f.titulo,
};

export default function Externos() {
  const { data, isLoading } = useExternosSinRut();
  const [fusionando, setFusionando] = useState<ExternoSinRut | null>(null);
  const [tab, setTab] = usePestanaUrl("ver", ["sin-correo", "pendientes", "con-cuenta"]);

  const registrados = data?.registrados ?? [];
  const pendientes = data?.pendientes ?? [];
  const sinCorreo = data?.sin_correo ?? [];

  // Tres estados del mismo trámite, y cada uno pide algo distinto: conseguir el
  // correo, dar el acceso, o revisar si es alguien que ya estaba. Van en pestañas
  // como en Estudiantes para no hacer scrollear entre tres tablas largas.
  const tabs = [
    { key: "sin-correo", label: "Sin correo", n: sinCorreo.length },
    { key: "pendientes", label: "Pendientes", n: pendientes.length },
    { key: "con-cuenta", label: "Con cuenta", n: registrados.length },
  ];

  return (
    <Box>
      <Typography variant="h4">Externos</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Los coguías que no son del DCC. Mientras estén solo anotados en un tema no tienen cuenta ni pueden evaluar: dales acceso desde
        acá.
      </Typography>

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={26} />
        </Stack>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            {tabs.map((t) => (
              // Un solo hijo: `Tab` apila en columna lo que le llegue suelto y el
              // contador terminaría bajo el nombre.
              <Tab
                key={t.key}
                value={t.key}
                label={
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                    {t.label} ({t.n})
                  </Box>
                }
                sx={{ textTransform: "none" }}
              />
            ))}
          </Tabs>

          {tab === "sin-correo" && <SinCorreo filas={sinCorreo} />}
          {tab === "pendientes" && <Pendientes filas={pendientes} />}
          {tab === "con-cuenta" && <Registrados filas={registrados} onFusionar={setFusionando} />}
        </>
      )}

      {fusionando && <FusionarDialog externo={fusionando} onClose={() => setFusionando(null)} />}
    </Box>
  );
}

/** El encabezado de cada pestaña: qué es esto y qué se hace acá. El contador ya lo
 *  dice la pestaña, así que no se repite. */
function Seccion({ ayuda, children }: { ayuda: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {ayuda}
      </Typography>
      {children}
    </Box>
  );
}

/** Los que todavía son texto: el grueso, y donde está el trabajo. */
function Pendientes({ filas }: { filas: CoguiaPendiente[] }) {
  const dar = useDarAccesoPorCorreo();
  const [yendo, setYendo] = useState("");
  const { orden, onSort } = useOrden();

  return (
    <Seccion ayuda="Anotados en un tema y nada más. Darles acceso los registra y engancha todos sus temas de una vez.">
      {dar.isError && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {mensajeDeError(dar.error, "No se pudo dar acceso.")}
        </Alert>
      )}
      {/* La fila desaparece de la lista al registrarla, así que sin esto no quedaba
          dicho lo principal: que a la persona le llegó el aviso y ya puede entrar. */}
      {dar.isSuccess && (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          {dar.data.persona.nombre} quedó registrado en {dar.data.temas} tema{dar.data.temas === 1 ? "" : "s"}.{" "}
          {dar.data.cuenta_creada
            ? "Le llegó un correo con el enlace para elegir su contraseña."
            : "Ya tenía cuenta, así que entra como siempre."}
        </Alert>
      )}
      {!filas.length ? (
        <Typography variant="body2" color="text.disabled">
          No queda ninguno pendiente.
        </Typography>
      ) : (
        <TablaKit
          columnas={[
            { label: "Persona", sortKey: "persona" },
            { label: "Correo", sortKey: "email" },
            { label: "Temas", sortKey: "temas" },
            "",
          ]}
          orden={orden}
          onSort={onSort}
        >
          <TableBody>
            {ordenar(filas, ACC_PENDIENTE, orden).map((f) => (
              <FilaKit key={f.email}>
                <CeldaKit>
                  {f.nombres[0]}
                  {f.nombres.length > 1 && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      · también como {f.nombres.slice(1).join(", ")}
                    </Typography>
                  )}
                  {/* Si el correo ya es de alguien, darle acceso lo engancha a esa
                      ficha en vez de crear otra. Mejor decirlo antes que después. */}
                  {f.ya_es && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ya está en el sistema como {f.ya_es}
                    </Typography>
                  )}
                </CeldaKit>
                <CeldaKit>{f.email}</CeldaKit>
                <CeldaKit>
                  <Etiqueta>{f.temas.length}</Etiqueta>
                </CeldaKit>
                <CeldaKit>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={dar.isPending}
                    onClick={() => {
                      setYendo(f.email);
                      dar.mutate(f.email);
                    }}
                  >
                    {dar.isPending && yendo === f.email ? "Registrando…" : "Darle acceso"}
                  </Button>
                </CeldaKit>
              </FilaKit>
            ))}
          </TableBody>
        </TablaKit>
      )}
    </Seccion>
  );
}

/** Los que ya tienen ficha y cuenta. */
function Registrados({ filas, onFusionar }: { filas: ExternoSinRut[]; onFusionar: (e: ExternoSinRut) => void }) {
  const { orden, onSort } = useOrden();
  return (
    <Seccion ayuda="Ya pueden entrar y evaluar. Si alguno es alguien que ya estaba con otro correo, júntalos: su historia queda en una sola ficha.">
      {!filas.length ? (
        <Typography variant="body2" color="text.disabled">
          Todavía no le has dado acceso a nadie.
        </Typography>
      ) : (
        <TablaKit
          columnas={[
            { label: "Persona", sortKey: "persona" },
            { label: "Correo", sortKey: "email" },
            { label: "Su historia", sortKey: "historia" },
            "",
          ]}
          orden={orden}
          onSort={onSort}
        >
          <TableBody>
            {ordenar(filas, ACC_REGISTRADO, orden).map((e) => (
              <FilaKit key={e.id}>
                <CeldaKit>
                  {e.nombre}
                  {!e.tiene_cuenta && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      · sin cuenta
                    </Typography>
                  )}
                </CeldaKit>
                <CeldaKit>{e.email || "—"}</CeldaKit>
                <CeldaKit>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {e.temas > 0 && <Etiqueta>{e.temas} tema(s)</Etiqueta>}
                    {e.procesos > 0 && <Etiqueta>{e.procesos} proceso(s)</Etiqueta>}
                    {e.comisiones > 0 && <Etiqueta>{e.comisiones} comisión(es)</Etiqueta>}
                    {e.evaluaciones > 0 && <Etiqueta>{e.evaluaciones} evaluación(es)</Etiqueta>}
                    {!e.temas && !e.procesos && !e.comisiones && !e.evaluaciones && (
                      <Typography variant="caption" color="text.disabled">
                        sin actividad
                      </Typography>
                    )}
                  </Stack>
                </CeldaKit>
                <CeldaKit>
                  <Button size="small" variant="outlined" onClick={() => onFusionar(e)}>
                    Es alguien que ya está…
                  </Button>
                </CeldaKit>
              </FilaKit>
            ))}
          </TableBody>
        </TablaKit>
      )}
    </Seccion>
  );
}

/** Anotados sin correo: hay que ir a pedirlo antes de poder hacer nada. */
function SinCorreo({ filas }: { filas: { id: number; titulo: string; nombre: string }[] }) {
  const { orden, onSort } = useOrden();
  if (!filas.length) {
    return (
      <Typography variant="body2" color="text.disabled">
        Todos los coguías externos anotados tienen correo.
      </Typography>
    );
  }
  return (
    <Seccion ayuda="Anotados solo con el nombre. Sin correo no hay a quién darle acceso: consigue el dato y agrégalo editando el tema.">
      <TablaKit
        columnas={[
          { label: "Persona", sortKey: "persona" },
          { label: "Tema", sortKey: "tema" },
        ]}
        orden={orden}
        onSort={onSort}
      >
        <TableBody>
          {ordenar(filas, ACC_SIN_CORREO, orden).map((f) => (
            <FilaKit key={f.id}>
              <CeldaKit>{f.nombre}</CeldaKit>
              <CeldaKit>
                {/* El enlace del kit: hereda el color de la celda. Un <Link> crudo
                    trae el azul del navegador, que no es de la paleta y en oscuro
                    se lee pésimo. */}
                <EnlaceTema id={f.id} titulo={f.titulo} />
              </CeldaKit>
            </FilaKit>
          ))}
        </TableBody>
      </TablaKit>
    </Seccion>
  );
}

/** Elegir con quién juntarlo, mirar qué se movería, y recién entonces juntarlos. */
function FusionarDialog({ externo, onClose }: { externo: ExternoSinRut; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [destino, setDestino] = useState<DocenteOpcion | null>(null);
  const { data: opciones, isLoading: buscando } = useDocentes(q, true);
  const fusionar = useFusionarPersona();

  // La previsualización se pide al elegir; el botón de juntar solo aparece cuando ya
  // se vio. Es irreversible: nadie debería apretarlo sin saber qué se lleva.
  const elegir = (d: DocenteOpcion | null) => {
    setDestino(d);
    fusionar.reset();
    if (d) fusionar.mutate({ id: externo.id, destinoId: d.id });
  };

  const previsualizado = fusionar.data && !fusionar.data.publicado;
  const listo = fusionar.data?.publicado;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Juntar con quien ya está — {externo.nombre}</DialogTitle>
      <DialogContent dividers>
        {listo ? (
          <Alert severity="success">
            Listo: la historia de {fusionar.data!.origen} quedó en la ficha de {fusionar.data!.destino}, y {externo.email} sigue
            llevando a esa persona.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Busca a la persona que ya está en el sistema. Todo lo de <strong>{externo.nombre}</strong> pasa a su ficha y esta
              desaparece; su correo ({externo.email}) queda registrado para que siga llevando a la misma persona.
            </Typography>

            <Autocomplete
              options={opciones ?? []}
              loading={buscando}
              value={destino}
              onChange={(_, d) => elegir(d)}
              onInputChange={(_, v) => setQ(v)}
              getOptionLabel={(o) => o.nombre}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Juntar con" placeholder="Busca por nombre…" />}
            />

            {fusionar.isPending && <CircularProgress size={22} sx={{ alignSelf: "center" }} />}

            {fusionar.isError && <Alert severity="error">{mensajeDeError(fusionar.error, "No se pueden juntar.")}</Alert>}

            {previsualizado && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Se moverán {fusionar.data!.total} cosa(s) a {fusionar.data!.destino}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {fusionar.data!.relaciones.map((r) => (
                    <li key={`${r.que}-${r.campo}`}>
                      <Typography variant="body2">
                        {r.que}: {r.n}
                      </Typography>
                    </li>
                  ))}
                </Box>
                {fusionar.data!.descartadas > 0 && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {fusionar.data!.descartadas} se descartan por estar repetidas en las dos fichas.
                  </Typography>
                )}
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Esto no se puede deshacer.
                </Typography>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={fusionar.isPending}>
          {listo ? "Cerrar" : "Cancelar"}
        </Button>
        {previsualizado && destino && (
          <Button
            variant="contained"
            color="error"
            disabled={fusionar.isPending}
            onClick={() => fusionar.mutate({ id: externo.id, destinoId: destino.id, commit: true })}
          >
            Juntarlas
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
